/** Named distractors that fit the stem and are not the keyed fact. */

export function topicDistractorBank(stem: string): string[] {
  const text = stem.toLowerCase()
  if (text.includes("amino acid") && text.includes("negative charge")) {
    return [
      "Lysine",
      "Glutamine",
      "Histidine",
      "Asparagine",
      "Arginine",
      "Alanine",
      "Leucine",
    ]
  }
  if (text.includes("citric") || text.includes("krebs")) {
    return [
      "Intermembrane space",
      "Cristae",
      "Cytosol",
      "Peroxisome",
      "Cytoplasm",
      "Nucleus",
    ]
  }
  if (text.includes("beta oxidation")) {
    return ["Nucleus", "Intermembrane space", "Cytosol"]
  }
  if (/chamber of the heart receives deoxygenated/i.test(stem)) {
    return ["Coronary sinus", "Pulmonary veins", "Inferior vena cava"]
  }
  if (/primary site of atp synthesis/i.test(stem)) {
    return ["Endoplasmic reticulum", "Peroxisome", "Plasma membrane"]
  }
  if (text.includes("heart failure") || text.includes("dyspnea")) {
    return [
      "Auscultate the lung bases for new crackles",
      "Check for jugular venous distention",
      "Obtain orthostatic blood pressures first",
      "Start a fluid bolus before assessing volume status",
    ]
  }
  if (text.includes("copd") || text.includes("desaturat")) {
    return [
      "Stop, seat the client, and coach pursed-lip breathing",
      "Increase the walk pace to build tolerance",
      "Raise supplemental oxygen without a reassessment",
      "Continue ambulation and document the reading later",
    ]
  }
  if (text.includes("insulin") || text.includes("hypoglyc")) {
    return [
      "Give 15 g of a fast-acting carbohydrate and recheck glucose",
      "Hold the next insulin dose and observe only",
      "Give a full meal before treating the low reading",
      "Increase the insulin infusion to correct the trend",
    ]
  }
  return [
    "Reassess the client and notify the provider",
    "Hold the next intervention and document only",
    "Escalate treatment without a new assessment",
    "Reassure the client and defer indicated monitoring",
  ]
}

export function unusedTopicDistractors(stem: string, used: string[]): string[] {
  const taken = new Set(
    used.map((value) => value.trim().toLowerCase()).filter(Boolean),
  )
  return topicDistractorBank(stem).filter(
    (value) => !taken.has(value.trim().toLowerCase()),
  )
}

function fillNamedSet(
  stem: string,
  used: string[],
  candidates: string[],
  size: number,
): string[] {
  const taken = new Set(
    used.map((value) => value.trim().toLowerCase()).filter(Boolean),
  )
  const out: string[] = []
  const push = (name: string) => {
    const key = name.trim().toLowerCase()
    if (!key || taken.has(key) || out.some((row) => row.toLowerCase() === key)) {
      return
    }
    out.push(name)
    taken.add(key)
  }
  for (const name of candidates) {
    if (out.length >= size) break
    push(name)
  }
  for (const name of unusedTopicDistractors(stem, [...used, ...out])) {
    if (out.length >= size) break
    push(name)
  }
  if (out.length < Math.min(2, size)) return []
  return out.slice(0, size)
}

/**
 * Complete distractor packages for one apply. Each row replaces All of the
 * above, None of the above, then leftover positional options, in that order.
 * Amino acid packages stay wrong for a negative-charge stem whose key is
 * Aspartate: no second acidic residue.
 */
export function topicCompleteDistractorSets(
  stem: string,
  used: string[],
  size: number,
): string[][] {
  const text = stem.toLowerCase()
  if (text.includes("amino acid") && text.includes("negative charge")) {
    return [
      fillNamedSet(stem, used, ["Lysine", "Glutamine", "Histidine"], size),
      fillNamedSet(stem, used, ["Lysine", "Asparagine", "Alanine"], size),
      fillNamedSet(stem, used, ["Arginine", "Glutamine", "Leucine"], size),
    ].filter((row) => row.length >= 2)
  }
  const unused = unusedTopicDistractors(stem, used)
  const rows: string[][] = []
  for (let index = 0; index < unused.length && rows.length < 2; index += size) {
    const slice = unused.slice(index, index + size)
    if (slice.length >= 2) rows.push(slice)
  }
  return rows
}

export function namedSetLabel(names: string[]): string {
  if (names.length <= 1) return names[0] ?? ""
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`
}
